import { APIConstraintEnforcementMode } from '@ardoq/api-types';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { getAllRelevantTriples } from 'createReferences/getAllRelevantTriples';
import { TripleWithContext } from 'createReferences/toTriplesWithContext';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import {
  dynamicReferenceCreation$,
  isArmedState,
} from './dynamicReferenceCreation$';
import { referenceCreationDependencies$ } from './referenceCreationDependencies$';

export type SourceTriples = {
  triples: TripleWithContext[];
  constraintsEnforcementMode: APIConstraintEnforcementMode;
};

const sourceTypeName$: Observable<string | null> = combineLatest([
  dynamicReferenceCreation$,
  loadedGraph$,
]).pipe(
  map(([gesture, { scopeData }]) => {
    if (!isArmedState(gesture)) return null;
    return (
      enhancedScopeDataOperations.getComponent(
        scopeData,
        gesture.sourceComponentId
      )?.type ?? null
    );
  }),
  distinctUntilChanged()
);

/**
 * Every triple the org metamodel and the data allow for the armed component's
 * type. Resolving them walks every model in the organization, so it is keyed on
 * the source component rather than recomputed as the pointer moves.
 *
 * Shared and subscribed by the overlay for as long as it is mounted, so the two
 * requests behind the dependencies happen once per visit to viewpoint mode
 * rather than once per drag — and a failed one is retried on the next visit
 * instead of being cached for the session.
 */
export const sourceTriples$: Observable<SourceTriples | null> = combineLatest([
  sourceTypeName$,
  referenceCreationDependencies$,
]).pipe(
  map(([componentTypeName, dependencies]) => {
    if (componentTypeName === null) return null;
    const { constraintsEnforcementMode, ...triplesDependencies } = dependencies;
    return {
      triples: Object.values(
        getAllRelevantTriples({ componentTypeName, ...triplesDependencies })
      ),
      constraintsEnforcementMode,
    };
  }),
  shareReplay({ bufferSize: 1, refCount: true })
);
